import { useEffect, useMemo, useState } from 'react';

import { Button } from '@playbooks/ui/components/button';
import { DialogFooter, DialogHeader, DialogTitle } from '@playbooks/ui/components/dialog';
import { Field, FieldDescription, FieldLabel } from '@playbooks/ui/components/field';
import { Input } from '@playbooks/ui/components/input';
import { Textarea } from '@playbooks/ui/components/textarea';
import { ToggleGroup, ToggleGroupItem } from '@playbooks/ui/components/toggle-group';

import { AgentDesignPalette } from 'components/agent/agent-design-palette';
import { useStore } from 'contexts';
import { parseAgentDesignMarkdown } from 'utils/agent-design-markdown';

const AgentDesignFlowForm = ({ model, onPrev, onNext, onCreate, onUpdate, projectLocal = false }) => {
	const [source, setSource] = useState('website');
	const [url, setUrl] = useState('');
	const [preview, setPreview] = useState<any>(null);
	const [tags, setTags] = useState<any[]>([]);
	const [design, setDesign] = useState<any>({
		name: '',
		description: '',
		markdown: '',
		visibility: 'private',
		tagIds: [],
	});
	const [previewing, setPreviewing] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const store = useStore();
	const parsed = useMemo(() => parseAgentDesignMarkdown(design.markdown), [design.markdown]);

	// Hooks
	useEffect(() => {
		store.query({ url: '/agent-design-tags' }).then(response => setTags(response.data || []));
	}, []);
	useEffect(() => {
		if (!model) {
			setSource('website');
			setUrl('');
			setPreview(null);
			setDesign({ name: '', description: '', markdown: '', visibility: 'private', tagIds: [] });
			return;
		}
		setSource(model.sourceType === 'import' ? 'github' : 'website');
		setUrl(model.sourceUrl || '');
		setPreview(model);
		setDesign({
			name: model.name || '',
			description: model.description || '',
			markdown: model.markdown || '',
			visibility: model.visibility || 'private',
			tagIds: (model.tags || []).map(tag => tag.id),
		});
	}, [model]);

	// Methods
	const onSource = value => {
		if (preview && !window.confirm('Discard this imported design and change the source?')) return;
		setSource(value);
		setUrl('');
		setPreview(null);
		setDesign({ name: '', description: '', markdown: '', visibility: 'private', tagIds: [] });
		setError('');
	};
	const onPreview = async () => {
		setPreviewing(true);
		setError('');
		try {
			const response = await store.createRecord({
				url: `/workspace/agent-design-previews/${source}`,
				data: { url: url.trim() },
			});
			setPreview(response.data);
			setDesign(current => ({
				...current,
				name: response.data.name || '',
				description: response.data.description || '',
				markdown: response.data.markdown || '',
			}));
		} catch (nextError) {
			setError(nextError?.message || 'The design could not be imported.');
		} finally {
			setPreviewing(false);
		}
	};
	const onRefresh = async () => {
		setPreviewing(true);
		setError('');
		try {
			const response = await store.createRecord({
				url: `/workspace/designs/${model.uuid}/refresh-preview`,
				data: {},
			});
			setPreview(response.data);
			setDesign(current => ({
				...current,
				name: response.data.name || '',
				description: response.data.description || '',
				markdown: response.data.markdown || '',
			}));
		} catch (nextError) {
			setError(nextError?.message || 'The design could not be refreshed.');
		} finally {
			setPreviewing(false);
		}
	};
	const onSave = async event => {
		event.preventDefault();
		if (!preview || parsed.errors.length) return;
		setSaving(true);
		setError('');
		try {
			const response = model
				? onUpdate
					? { data: await onUpdate(design) }
					: preview?.previewId
						? await store.updateRecord({
								url: `/workspace/designs/${model.uuid}/refresh`,
								data: { previewId: preview.previewId, ...design },
							})
						: await store.updateRecord({ url: `/agent-designs/${model.uuid}`, data: design })
				: onCreate
					? { data: await onCreate({ ...design, sourceType: source === 'website' ? 'url' : 'import', sourceUrl: url }) }
					: await store.createRecord({
							url: '/workspace/designs/imports',
							data: { previewId: preview.previewId, ...design },
						});
			await onNext(response.data);
		} catch (nextError) {
			setError(nextError?.message || 'The design could not be saved.');
		} finally {
			setSaving(false);
		}
	};

	// Render
	return (
		<form onSubmit={onSave}>
			<DialogHeader>
				<DialogTitle>{model ? 'Edit design' : 'Add a design'}</DialogTitle>
			</DialogHeader>
			<div className='grid gap-5'>
				{!model ? (
					<ToggleGroup value={[source]} onValueChange={values => values[0] && onSource(values[0])} className='w-full'>
						<ToggleGroupItem value='website' className='w-1/2'>
							Website
						</ToggleGroupItem>
						<ToggleGroupItem value='github' className='w-1/2'>
							GitHub
						</ToggleGroupItem>
					</ToggleGroup>
				) : null}
				{!model ? (
					<Field>
						<FieldLabel>{source === 'website' ? 'Website URL' : 'GitHub DESIGN.md URL'}</FieldLabel>
						<div className='flex gap-2'>
							<Input
								type='url'
								value={url}
								placeholder={
									source === 'website' ? 'https://example.com' : 'https://github.com/owner/repo/blob/main/DESIGN.md'
								}
								onChange={event => setUrl(event.target.value)}
							/>
							<Button type='button' isLoading={previewing} disabled={!url.trim()} onClick={onPreview}>
								{source === 'website' ? 'Extract' : 'Import'}
							</Button>
						</div>
						<FieldDescription>
							{source === 'website'
								? 'Creates an editable design starting point from publicly visible styles.'
								: 'V1 supports public repositories and self-contained DESIGN.md files. Saved designs remain editable.'}
						</FieldDescription>
					</Field>
				) : null}
				{model && ['url', 'import'].includes(model.sourceType) && model.sourceUrl ? (
					<Field>
						<FieldLabel>Source</FieldLabel>
						<div className='flex items-center gap-2'>
							<Input value={model.sourceUrl} readOnly className='min-w-0 grow' />
							<Button type='button' variant='secondary' isLoading={previewing} onClick={onRefresh}>
								Refresh
							</Button>
						</div>
						<FieldDescription>Refresh loads the current source into this form without saving it.</FieldDescription>
					</Field>
				) : null}
				{preview ? (
					<>
						<FieldDescription>
							{projectLocal
								? 'This design is local to this project and does not change its workspace source.'
								: 'This design is private to the workspace. Marketplace publishing is curated by Playbooks.'}
						</FieldDescription>
						<Field>
							<FieldLabel>Tags</FieldLabel>
							<ToggleGroup
								value={(design.tagIds || []).map(String)}
								onValueChange={values => setDesign({ ...design, tagIds: values.map(Number) })}
								className='flex w-full flex-wrap justify-start'>
								{tags.map(tag => (
									<ToggleGroupItem key={tag.id} value={String(tag.id)}>
										{tag.name}
									</ToggleGroupItem>
								))}
							</ToggleGroup>
							<FieldDescription>Curated labels help people and agents discover this design.</FieldDescription>
						</Field>
						<Field>
							<FieldLabel>Name</FieldLabel>
							<Input value={design.name} onChange={event => setDesign({ ...design, name: event.target.value })} />
						</Field>
						<Field>
							<FieldLabel>Description</FieldLabel>
							<Textarea
								rows={3}
								value={design.description}
								onChange={event => setDesign({ ...design, description: event.target.value })}
							/>
						</Field>
						<Field>
							<div className='flex-between gap-3'>
								<FieldLabel>DESIGN.md</FieldLabel>
								<AgentDesignPalette colors={parsed.colors} />
							</div>
							<Textarea
								rows={14}
								value={design.markdown}
								onChange={event => setDesign({ ...design, markdown: event.target.value })}
								className='min-h-72 resize-y font-mono text-xs'
							/>
						</Field>
						<div className='flex-between gap-3 text-xs text-foreground-body'>
							{preview.sourceUrl ? <p className='truncate'>Source: {preview.sourceUrl}</p> : <span />}
							{source === 'website' && preview.previewId ? (
								<span className='shrink-0'>Automatically inferred · Review before saving</span>
							) : null}
						</div>
					</>
				) : null}
				{[...(preview ? parsed.errors : []), error].filter(Boolean).map(message => (
					<p key={message} className='text-sm text-danger'>
						{message}
					</p>
				))}
			</div>
			<DialogFooter>
				<Button type='button' variant='secondary' onClick={onPrev}>
					Back
				</Button>
				<Button type='submit' isLoading={saving} disabled={!preview || Boolean(parsed.errors.length)}>
					Save
				</Button>
			</DialogFooter>
		</form>
	);
};

export { AgentDesignFlowForm };
